import React, { useState } from 'react';
import { Box, Typography, Container, TextField, Button, MenuItem, Alert, CircularProgress } from '@mui/material';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../contexts/AuthContext';
import Header from '../components/Header';

function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const subjectOptions = [
    { value: 'Book a demo', label: 'Book a demo' },
    { value: 'Partnership', label: 'Partnership' },
    { value: 'Other', label: 'Other' }
  ];

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // The document shape the Trigger Email extension reads. The Site's
      // Firestore rules (SiteBackend firestore.rules) accept exactly this
      // shape from anyone, with the recipient pinned, so the public form
      // cannot be used to email anyone else.
      const mailDocument = {
        to: 'josh@bimanalytics.ai',
        message: {
          subject: form.subject,
          text: `Message from site:\nFrom: ${form.email}\nName: ${form.name}\nMessage:\n${form.message}`
        }
      };

      // Add document to Firestore 'mail' collection
      await addDoc(collection(db, 'mail'), mailDocument);
      
      setSubmitted(true);
      setForm({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ContactPage">
      <Header />
      <Container sx={{ padding: '4rem 0' }}>
        <Typography variant="h2" align="center" sx={{ mb: 3 }}>
          Contact Us
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: '600px', margin: '0 auto 1rem auto' }}>
            {error}
          </Alert>
        )}
        
        {submitted ? (
          <Typography variant="h5" align="center" sx={{ mb: 3 }}>
            Thank you for your message! We will get back to you shortly.
          </Typography>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            <TextField
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              variant="outlined"
              required
              inputProps={{ maxLength: 200 }}
              InputProps={{ 
                style: { 
                  color: '#0B1929',
                  backgroundColor: '#fff',
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '& fieldset': { 
                    borderColor: '#D1D5DB',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px',
                    boxShadow: '0 0 0 3px rgba(13, 115, 119, 0.12)'
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748B',
                  '&.Mui-focused': {
                    color: '#0D7377'
                  }
                }
              }}
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              variant="outlined"
              required
              inputProps={{ maxLength: 254 }}
              InputProps={{ 
                style: { 
                  color: '#0B1929',
                  backgroundColor: '#fff',
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '& fieldset': { 
                    borderColor: '#D1D5DB',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px',
                    boxShadow: '0 0 0 3px rgba(13, 115, 119, 0.12)'
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748B',
                  '&.Mui-focused': {
                    color: '#0D7377'
                  }
                }
              }}
            />
            <TextField
              select
              label="Subject"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              variant="outlined"
              required
              InputProps={{ 
                style: { 
                  color: '#0B1929',
                  backgroundColor: '#fff',
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '& fieldset': { 
                    borderColor: '#D1D5DB',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px',
                    boxShadow: '0 0 0 3px rgba(13, 115, 119, 0.12)'
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748B',
                  '&.Mui-focused': {
                    color: '#0D7377'
                  }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#fff',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    '& .MuiMenuItem-root': {
                      color: '#0B1929',
                      '&:hover': {
                        backgroundColor: '#F1F5F9'
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#E0F2FE',
                        '&:hover': {
                          backgroundColor: '#B0E7FF'
                        }
                      }
                    }
                  }
                }
              }}
            >
              {subjectOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Message"
              name="message"
              value={form.message}
              onChange={handleChange}
              variant="outlined"
              required
              multiline
              rows={4}
              inputProps={{ maxLength: 5000 }}
              InputProps={{ 
                style: { 
                  color: '#0B1929',
                  backgroundColor: '#fff',
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '& fieldset': { 
                    borderColor: '#D1D5DB',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#0D7377',
                    borderWidth: '2px',
                    boxShadow: '0 0 0 3px rgba(13, 115, 119, 0.12)'
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748B',
                  '&.Mui-focused': {
                    color: '#0D7377'
                  }
                }
              }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#0D7377',
                '&:hover': {
                  backgroundColor: '#4080C3',
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </Button>
          </Box>
        )}
      </Container>
    </div>
  );
}

export default ContactPage;
